import { useState, useEffect, useRef } from "react";
import DSBox from "views/system-design/DSBox";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSIconButton from "views/system-design/DSIconButton";
import DSTooltip from "views/system-design/DSTooltip";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import { Ticket } from "../../types";
import { useTickets } from "../../context/TicketsContext";
import { useTicketDetail } from "../../hooks/useTicketQueries";
import { useIsMobile } from "../../hooks/useIsMobile";
import TicketInfoSection from "./TicketInfoSection";
import TicketSidePanel from "./TicketSidePanel";
import TicketComments from "./TicketComments";
import TicketSubtasks from "./TicketSubtasks";
import TicketAttachments from "./TicketAttachments";
import TicketTimestamps from "./TicketTimestamps";
import DSErrorButton from "../../../system-design/DSErrorButton";

interface TicketDetailContentProps {
  ticket: Ticket;
  onClose: () => void;
  layout: "drawer" | "modal";
}

function CopyLinkButton({ ticket }: { ticket: Ticket }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const handleCopy = () => {
    const url = `${window.location.origin}${window.location.pathname}?ticket=${ticket.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <DSTooltip title={copied ? "Copied!" : "Copy link"}>
      <DSIconButton onClick={handleCopy} size="small">
        <LinkIcon fontSize="small" />
      </DSIconButton>
    </DSTooltip>
  );
}

function DeleteButton({
  ticket,
  onClose,
}: {
  ticket: Ticket;
  onClose: () => void;
}) {
  const { deleteTicket } = useTickets();

  return (
    <DSErrorButton
      color="error"
      size="small"
      startIcon={<DeleteIcon />}
      onClick={() => {
        deleteTicket(ticket.id);
        onClose();
      }}
      sx={{ mt: 3, width: "100%" }}
    >
      Delete Ticket
    </DSErrorButton>
  );
}

function DrawerLayout({
  ticket,
  onClose,
}: {
  ticket: Ticket;
  onClose: () => void;
}) {
  return (
    <DSBox sx={{ overflowY: "auto", flex: 1, px: 3, pb: 3 }}>
      <TicketInfoSection ticket={ticket} />

      <DSBox
        sx={{
          borderTop: 1,
          borderColor: "divider",
          pt: 2,
          mb: 2,
        }}
      >
        <TicketSidePanel ticket={ticket} />
      </DSBox>

      <TicketSubtasks ticket={ticket} />
      <TicketAttachments ticket={ticket} />
      <TicketComments ticket={ticket} />

      <DeleteButton ticket={ticket} onClose={onClose} />
    </DSBox>
  );
}

function ModalLayout({
  ticket,
  onClose,
}: {
  ticket: Ticket;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();

  return (
    <DSDialogContent sx={{ p: 0 }}>
      <DSBox
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          minHeight: isMobile ? "auto" : 500,
        }}
      >
        <DSBox sx={{ flex: 1, p: isMobile ? 2 : 3, overflowY: "auto" }}>
          <TicketInfoSection ticket={ticket} />
          <TicketSubtasks ticket={ticket} />
          <TicketAttachments ticket={ticket} />
          <TicketComments ticket={ticket} />
        </DSBox>

        <DSBox
          sx={{
            width: isMobile ? "100%" : 280,
            borderLeft: isMobile ? 0 : 1,
            borderTop: isMobile ? 1 : 0,
            borderColor: "divider",
            p: 2,
            bgcolor: "white.5",
          }}
        >
          <TicketSidePanel ticket={ticket} />
          <DeleteButton ticket={ticket} onClose={onClose} />
        </DSBox>
      </DSBox>
    </DSDialogContent>
  );
}

export default function TicketDetailContent({
  ticket,
  onClose,
  layout,
}: TicketDetailContentProps) {
  const current = useTicketDetail(ticket);

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 2,
          pb: layout === "drawer" ? 1 : 0,
        }}
      >
        <TicketTimestamps ticket={current} />
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CopyLinkButton ticket={current} />
          <DSIconButton onClick={onClose} size="small">
            <CloseIcon />
          </DSIconButton>
        </DSBox>
      </DSBox>

      {layout === "drawer" ? (
        <DrawerLayout ticket={current} onClose={onClose} />
      ) : (
        <ModalLayout ticket={current} onClose={onClose} />
      )}
    </>
  );
}
